import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import {
  AlignLeft,
  CheckSquare,
  Circle,
  Eye,
  GripVertical,
  ListChecks,
  Plus,
  Trash2,
  Type,
  X,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { DragHandleProps, EditableQuestion, QuestionType } from "../lib/admin-vendor-form.utils";
import { VENDOR_FORM_LIMITS, createOptionUid } from "../lib/admin-vendor-form.utils";

const TYPE_ICON: Record<QuestionType, typeof Type> = {
  TEXT: Type,
  TEXTAREA: AlignLeft,
  SELECT: ListChecks,
};

interface QuestionEditorProps {
  question: EditableQuestion;
  index: number;
  onChange: (patch: Partial<EditableQuestion>) => void;
  onRemove: () => void;
  dragHandle?: DragHandleProps;
}

export function QuestionEditor({
  question,
  index,
  onChange,
  onRemove,
  dragHandle,
}: QuestionEditorProps) {
  const { t } = useTranslation();
  const options = question.options ?? [];
  const optionUids = question.optionUids ?? [];
  const isSelect = question.type === "SELECT";
  const allowMultiple = question.allowMultiple ?? true;
  // Count only non-empty options — the API strips blanks before validating, so
  // two blank slots must still read as "too few".
  const tooFewOptions =
    isSelect &&
    options.filter((o) => o.trim()).length < VENDOR_FORM_LIMITS.selectOptionMinCount;
  const promptLength = question.prompt.length;
  const promptNearLimit = promptLength > VENDOR_FORM_LIMITS.questionPromptMax * 0.8;

  const optionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function setType(next: QuestionType) {
    if (next === question.type) return;
    if (next === "SELECT") {
      if (options.length >= VENDOR_FORM_LIMITS.selectOptionMinCount) {
        onChange({ type: next });
      } else {
        onChange({ type: next, options: ["", ""], optionUids: [createOptionUid(), createOptionUid()] });
      }
    } else {
      onChange({ type: next, options: [], optionUids: [] });
    }
  }

  function updateOption(optionIndex: number, value: string) {
    onChange({
      options: options.map((o, i) => (i === optionIndex ? value : o)),
    });
  }

  function addOption() {
    if (options.length >= VENDOR_FORM_LIMITS.questionOptionsMaxCount) return;
    onChange({ options: [...options, ""], optionUids: [...optionUids, createOptionUid()] });
  }

  function removeOption(optionIndex: number) {
    onChange({
      options: options.filter((_, i) => i !== optionIndex),
      optionUids: optionUids.filter((_, i) => i !== optionIndex),
    });
  }

  function handleOptionDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = optionUids.indexOf(active.id as string);
    const newIndex = optionUids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange({
      options: arrayMove(options, oldIndex, newIndex),
      optionUids: arrayMove(optionUids, oldIndex, newIndex),
    });
  }

  return (
    <div className="group/question relative -mx-3 rounded-lg px-3 py-4 transition-colors first:pt-1 hover:bg-muted/30">
      {/* Header */}
      <div className="mb-3 flex items-center gap-2">
        <QuestionDragHandle dragHandle={dragHandle} />
        <span className="text-xs font-semibold tabular-nums text-foreground">
          {t("marketplace.admin.form.questionN", {
            n: index + 1,
            defaultValue: "Question {{n}}",
          })}
        </span>
        {question.isRequired && (
          <Badge variant="default" className="text-[10px]">
            {t("marketplace.admin.form.requiredBadge", { defaultValue: "Required" })}
          </Badge>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-auto h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover/question:opacity-100"
              onClick={onRemove}
              aria-label={t("marketplace.admin.form.removeQuestion", {
                defaultValue: "Remove question",
              })}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {t("marketplace.admin.form.removeQuestion", { defaultValue: "Remove question" })}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-4 pl-7">
        {/* Prompt */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${question.uid}-prompt`}>
            {t("marketplace.admin.form.questionPrompt")}
          </Label>
          <Input
            id={`${question.uid}-prompt`}
            value={question.prompt}
            required
            maxLength={VENDOR_FORM_LIMITS.questionPromptMax}
            placeholder={t("marketplace.admin.form.questionPromptPlaceholder", {
              defaultValue: "e.g. What is your brand's tone of voice?",
            })}
            onChange={(e) => onChange({ prompt: e.target.value })}
          />
          {promptNearLimit && (
            <span className="self-end text-[10px] tabular-nums text-muted-foreground">
              {promptLength}/{VENDOR_FORM_LIMITS.questionPromptMax}
            </span>
          )}
        </div>

        {/* Answer type — segmented control */}
        <div className="flex flex-col gap-1.5">
          <Label>{t("marketplace.admin.form.questionType")}</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            value={question.type}
            onValueChange={(v) => v && setType(v as QuestionType)}
            className="w-full gap-1.5"
          >
            {(["TEXT", "TEXTAREA", "SELECT"] as const).map((type) => {
              const TypeIcon = TYPE_ICON[type];
              const active = question.type === type;
              return (
                <ToggleGroupItem
                  key={type}
                  value={type}
                  className={cn(
                    "h-auto flex-1 flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left",
                    active
                      ? "border-primary/50 bg-primary/5"
                      : "border-border hover:border-border hover:bg-muted/50",
                  )}
                >
                  <span className="flex items-center gap-1.5 text-xs font-medium">
                    <TypeIcon className="h-3.5 w-3.5" aria-hidden />
                    {t(
                      type === "TEXT"
                        ? "marketplace.admin.form.typeText"
                        : type === "TEXTAREA"
                          ? "marketplace.admin.form.typeTextarea"
                          : "marketplace.admin.form.typeSelect",
                    )}
                  </span>
                  <span className="hidden text-[10px] font-normal text-muted-foreground sm:block">
                    {t(
                      type === "TEXT"
                        ? "marketplace.admin.form.typeTextDesc"
                        : type === "TEXTAREA"
                          ? "marketplace.admin.form.typeTextareaDesc"
                          : "marketplace.admin.form.typeSelectDesc",
                      {
                        defaultValue:
                          type === "TEXT"
                            ? "Single line"
                            : type === "TEXTAREA"
                              ? "Multi-line"
                              : "Pick one or more",
                      },
                    )}
                  </span>
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>

        {/* Options editor (SELECT only) */}
        {isSelect && (
          <div className="flex flex-col gap-2 rounded-md border border-border/60 bg-muted/20 p-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">
                {t("marketplace.admin.form.questionOptions")}
              </Label>
              <span
                className={cn(
                  "text-[10px] tabular-nums",
                  tooFewOptions ? "text-destructive" : "text-muted-foreground",
                )}
              >
                {options.length}/{VENDOR_FORM_LIMITS.questionOptionsMaxCount}
              </span>
            </div>

            {/* Single vs multiple selection mode */}
            <ToggleGroup
              type="single"
              variant="outline"
              value={allowMultiple ? "multiple" : "single"}
              onValueChange={(v) => v && onChange({ allowMultiple: v === "multiple" })}
              className="gap-1.5"
            >
              <ToggleGroupItem
                value="single"
                className={cn(
                  "h-7 gap-1.5 rounded-md border px-3 text-xs",
                  !allowMultiple
                    ? "border-primary/50 bg-primary/5 text-primary"
                    : "border-border hover:bg-muted/50",
                )}
              >
                <Circle className="h-3 w-3" aria-hidden />
                {t("marketplace.admin.form.selectSingle", { defaultValue: "Single choice" })}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="multiple"
                className={cn(
                  "h-7 gap-1.5 rounded-md border px-3 text-xs",
                  allowMultiple
                    ? "border-primary/50 bg-primary/5 text-primary"
                    : "border-border hover:bg-muted/50",
                )}
              >
                <CheckSquare className="h-3 w-3" aria-hidden />
                {t("marketplace.admin.form.selectMultiple", { defaultValue: "Multiple choice" })}
              </ToggleGroupItem>
            </ToggleGroup>

            <DndContext
              sensors={optionSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleOptionDragEnd}
            >
              <SortableContext items={optionUids} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-1.5">
                  {options.map((option, optionIndex) => (
                    <SortableOption
                      key={optionUids[optionIndex] ?? `opt-${optionIndex}`}
                      id={optionUids[optionIndex] ?? `opt-${optionIndex}`}
                      value={option}
                      index={optionIndex}
                      canRemove={options.length > VENDOR_FORM_LIMITS.selectOptionMinCount}
                      onChange={(v) => updateOption(optionIndex, v)}
                      onRemove={() => removeOption(optionIndex)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {tooFewOptions && (
              <p className="text-[11px] text-destructive">
                {t("marketplace.admin.form.optionsMinError", {
                  count: VENDOR_FORM_LIMITS.selectOptionMinCount,
                  defaultValue: "Add at least {{count}} options.",
                })}
              </p>
            )}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-start text-primary hover:text-primary"
              disabled={options.length >= VENDOR_FORM_LIMITS.questionOptionsMaxCount}
              onClick={addOption}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              {t("marketplace.admin.form.addOption", { defaultValue: "Add option" })}
            </Button>
          </div>
        )}

        {/* Live preview + required toggle */}
        <div className="flex flex-col gap-2 border-t border-border/60 pt-3">
          <QuestionPreview question={question} />

          <label className="mt-1 flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
            <span className="flex flex-col">
              <span className="text-sm font-medium">
                {t("marketplace.admin.form.questionIsRequired")}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("marketplace.admin.form.questionIsRequiredHint", {
                  defaultValue: "Tenants must answer before connecting.",
                })}
              </span>
            </span>
            <Switch
              checked={question.isRequired ?? false}
              onCheckedChange={(v) => onChange({ isRequired: v })}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function QuestionDragHandle({ dragHandle }: { dragHandle?: DragHandleProps }) {
  const { t } = useTranslation();
  if (!dragHandle) {
    return <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden />;
  }
  return (
    <button
      type="button"
      aria-label={t("common.dragToReorder")}
      className="-ml-1 flex h-7 w-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/50 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
      {...dragHandle.attributes}
      {...dragHandle.listeners}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}

function SortableOption({
  id,
  value,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  id: string;
  value: string;
  index: number;
  canRemove: boolean;
  onChange: (value: string) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? "relative" : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-1.5">
      <button
        type="button"
        aria-label={t("common.dragToReorder")}
        className="flex h-7 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/40 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <Input
        value={value}
        maxLength={VENDOR_FORM_LIMITS.questionOptionMax}
        placeholder={t("marketplace.admin.form.optionPlaceholder", {
          n: index + 1,
          defaultValue: "Option {{n}}",
        })}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 flex-1"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive disabled:opacity-40"
        disabled={!canRemove}
        onClick={onRemove}
        aria-label={t("marketplace.admin.form.removeOption", {
          defaultValue: "Remove option",
        })}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/** Read-only render of how the answer field appears to a connecting tenant. */
function QuestionPreview({ question }: { question: EditableQuestion }) {
  const { t } = useTranslation();
  const allowMultiple = question.allowMultiple ?? true;
  const prompt =
    question.prompt.trim() ||
    t("marketplace.admin.form.previewPromptFallback", { defaultValue: "Untitled question" });

  return (
    <div className="overflow-hidden rounded-md border border-dashed border-border bg-muted/30">
      {/* Caption makes it unmistakable this block is a non-interactive mock. */}
      <div className="flex items-center gap-1.5 border-b border-dashed border-border bg-muted/40 px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
        <Eye className="h-3 w-3 shrink-0" aria-hidden />
        {t("marketplace.admin.form.previewCaption", {
          defaultValue: "Example — how tenants will see this question",
        })}
      </div>

      {/* The mock answer controls are decorative only. */}
      <div className="pointer-events-none select-none p-3" aria-hidden>
        <p className="mb-2 text-xs font-medium text-foreground">
          {prompt}
          {question.isRequired && <span className="ml-0.5 text-destructive">*</span>}
        </p>

        {question.type === "TEXT" && (
          <div className="h-8 w-full rounded-md border border-border bg-background/60" />
        )}

        {question.type === "TEXTAREA" && (
          <div className="h-14 w-full rounded-md border border-border bg-background/60" />
        )}

        {question.type === "SELECT" && (
          <div className="flex flex-col gap-1.5">
          {(question.options ?? []).filter(Boolean).length === 0 ? (
            <span className="text-[11px] italic text-muted-foreground/60">
              {t("marketplace.admin.form.previewNoOptions", {
                defaultValue: "No options yet",
              })}
            </span>
          ) : (
            <>
              {/* Checkbox squares for multiple choice, radio circles for single choice. */}
              {(question.options ?? [])
                .filter(Boolean)
                .map((option, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 border border-muted-foreground/40 bg-background",
                        allowMultiple ? "rounded-[3px]" : "rounded-full",
                      )}
                      aria-hidden
                    />
                    {option}
                  </span>
                ))}
              <span className="mt-0.5 text-[10px] text-muted-foreground/60">
                {allowMultiple
                  ? t("marketplace.admin.form.selectMultiHint", {
                      defaultValue: "Tenants can select multiple options.",
                    })
                  : t("marketplace.admin.form.selectSingleHint", {
                      defaultValue: "Tenants can select one option.",
                    })}
              </span>
            </>
          )}
          </div>
        )}
      </div>
    </div>
  );
}
